import React from 'react';
import { GuidePanel, SubHeading } from '../GuidePanel';
import { countSteps } from '../StepList';
import { MapChip } from '../LinkChip';
import { ValidationSummary } from '../ValidationBadge';
import { NpcCard } from '../NpcCard';
import { QuestBlock } from '../quest/QuestBlock';
import { useNpcLocation } from '../../../hooks/useNpcLocation';
import { npcLocationValidation } from '../../../utils/npcLocation';
import { thirdEvolution as t } from '../../../data/guide/evolutions';
import { questTitle } from '../../../data/guide/quests';
import type { Checklist } from '../../../hooks/useChecklist';
import type { WorldLookup } from '../../../utils/progressionLookup';
import type { CharacterClass } from '../../../types/progression';
type Props = {
  cls?: CharacterClass;
  list: Checklist;
  lookup?: WorldLookup;
  status: string;
};

/** Tercera evolución: misiones 4, 5 y 6, con progreso visible. */
export function ThirdEvolutionSection({
  cls,
  list,
  lookup,
  status
}: Props) {
  const stages = [{
    id: 'etapa-1',
    questId: 4,
    steps: t.stage1.steps
  }, {
    id: 'etapa-2',
    questId: 5,
    steps: t.stage2.steps
  }, {
    id: 'etapa-3',
    questId: 6,
    steps: t.stage3.steps
  }];
  const locs = [useNpcLocation('devin'), useNpcLocation('quarel'), useNpcLocation('gatekeeper')];
  const npcRows = locs.map(l => l ? npcLocationValidation(l) : {
    status: 'pendiente' as const,
    detail: ''
  });
  const rank = {
    documentado: 3,
    admin: 3,
    clasica: 2,
    pendiente: 1
  } as const;
  const worst = npcRows.reduce((a, b) => rank[b.status] < rank[a.status] ? b : a);
  const progress = stages.map(s => ({
    ...s,
    done: countSteps(s.steps, list, `tercera-${s.questId}`)
  }));
  return <GuidePanel id="tercera" eyebrow={cls ? `${cls.second ?? cls.base} → ${cls.third}` : 'Tercera evolución'} title="Barracks y Refuge" data-id="element-799" data-mp="1pl443zsrv1bv">
      <ValidationSummary rows={[{
      label: 'Nivel, Zen, misión previa y clases',
      status: 'documentado',
      detail: 'Confirmado por la configuración exportada; MG, DL y RF tienen una regla propia para la misión 4'
    }, {
      label: 'Materiales y bajas requeridas',
      status: 'documentado',
      detail: '1 de cada material; 20 Balram, 20 Death Spirit, 20 Soram; 1 Dark Elf'
    }, {
      label: 'Dónde se obtienen los materiales',
      status: 'documentado',
      detail: 'Drops de misión por clase de monstruo; requieren la misión aceptada'
    }, {
      label: 'Ubicación de los NPC',
      status: worst.status,
      detail: ['Devin', 'Quarel', 'Gatekeeper'].map((n, i) => `${n}: ${npcRows[i].detail}`).join(' · ')
    }, {
      label: 'Prueba dentro del juego',
      status: 'sin-probar'
    }]} data-id="element-800" data-mp="x26wj6rxvx9u" />

      <ol className="grid grid-cols-3 gap-2" aria-label="Progreso de las etapas" data-id="element-801" data-mp="2i77b9pmgabvs">
        {progress.map((s, i) => {
        const complete = s.done === s.steps.length;
        return <li key={s.id} data-id="element-802" data-mp="3tn4aq7rx0e79" data-mpi={i}>
              <a href={`#tercera-${s.id}`} className={`block rounded-2xl border p-2.5 transition-colors duration-150 ${complete ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-[#0A1628] hover:border-[#D4AF37]/40'}`} data-id="element-803" data-mp="3tluezp8jbi3s">
                <span className="block text-[11px] text-gray-400" data-id="element-804" data-mp="cjq7p9z2ybfu">Etapa {i + 1}</span>
                <span className="block text-xs font-semibold leading-tight text-white sm:text-sm" data-id="element-805" data-mp="7lyco735297f">{questTitle(s.questId).replace('Devin, ', '')}</span>
                <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-white/10" aria-hidden="true" data-id="element-806" data-mp="3uyqvbu9jylbk">
                  <span className="block h-full rounded-full bg-[#D4AF37] transition-[width] duration-200 ease-out" style={{
                width: `${s.done / s.steps.length * 100}%`
              }} data-id="element-807" data-mp="3pzo4bdqeqfml" />
                </span>
                <span className="mt-1 block text-[11px] text-gray-500" data-id="element-808" data-mp="3g0ov9u1qchz2">{s.done}/{s.steps.length}</span>
              </a>
            </li>;
      })}
      </ol>

      {stages.map((s, i) => <div key={s.id} id={`tercera-${s.id}`} className="scroll-mt-28 space-y-3" data-id="element-809" data-mp="1ead9zxuug4wc" data-mpi={i}>
          <SubHeading data-id="element-810" data-mp="1a8jgqamza367">Etapa {i + 1} · {questTitle(s.questId)}</SubHeading>
          {i === 1 && <div className="space-y-2" data-id="element-811" data-mp="1hbpz4ze50jek">
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300" data-id="element-812" data-mp="1hcrl3ywoyqb2">Mapa: <MapChip name={t.stage2.map} lookup={lookup} data-id="element-813" data-mp="1mat5cn5usvo4" /></div>
              <NpcCard npcId="quarel" lookup={lookup} data-id="element-814" data-mp="18as2j2613e60" />
            </div>}
          {i === 2 && <div className="space-y-2" data-id="element-815" data-mp="1cc3mos64v76t">
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300" data-id="element-816" data-mp="1cb26yae69wir">Mapa: <MapChip name={t.stage3.map} lookup={lookup} data-id="element-817" data-mp="ykoz081a6htf" /></div>
              <NpcCard npcId="gatekeeper" lookup={lookup} data-id="element-818" data-mp="13boz1m5p5g05" />
            </div>}
          <QuestBlock questId={s.questId} cls={cls} list={list} prefix={`tercera-${s.questId}`} lookup={lookup} status={status} steps={s.steps} data-id="element-819" data-mp="13di795vyk8a9" />
        </div>)}
    </GuidePanel>;
}